import cn from "classnames";
import { type FC, useEffect, useRef, useState } from "react";
import { IconChevronDown } from "./icons";

export type ExportFormat = "csv" | "jsonl";

interface ExportButtonProps {
  onExport: (format: ExportFormat) => void;
  isExporting: boolean;
  disabled?: boolean;
}

const FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: "CSV",
  jsonl: "JSON",
};

export const ExportButton: FC<ExportButtonProps> = ({
  onExport,
  isExporting,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const handleSelect = (format: ExportFormat) => {
    setIsOpen(false);
    onExport(format);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isExporting || disabled}
        className={cn(
          "flex items-center gap-1 whitespace-nowrap",
          "border border-black-100 bg-white text-black text-xs font-medium",
          "px-2 py-2 rounded-md shadow-sm",
          "hover:bg-black-100 hover:border-black-300",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black-200",
          { "opacity-50 cursor-not-allowed": isExporting || disabled },
        )}
      >
        Export
        <IconChevronDown variant="sm" />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 mt-1 z-10",
            "bg-white border border-black-100 rounded-md shadow p-2",
            "min-w-[75px]",
          )}
        >
          {(["csv", "jsonl"] as const).map((format) => (
            <button
              key={format}
              type="button"
              onClick={() => handleSelect(format)}
              className={cn(
                "flex items-center justify-between w-full",
                "p-2 rounded text-xs text-left",
                "hover:bg-black-100",
              )}
            >
              <span>{FORMAT_LABELS[format]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
